
import { Task, Quadrant, AnalyticsMetrics, User, UserTier, MetricKey, DailySnapshot } from '../types';

export const analyticsService = {
  calculateMetrics(tasks: Task[], users: User[] = []): AnalyticsMetrics {
    const total = tasks.length || 1;
    const completedTasks = tasks.filter(t => t.isCompleted || t.status === 'COMPLETED');
    
    // 1. Priori Score: Weighted points by impact (Q1 > Q2 > Q3 > Q4)
    let score = 0;
    completedTasks.forEach(task => {
      if (task.currentQuadrant === Quadrant.DO) score += 25;
      else if (task.currentQuadrant === Quadrant.SCHEDULE) score += 15;
      else if (task.currentQuadrant === Quadrant.DELEGATE) score += 5;
      else if (task.currentQuadrant === Quadrant.ELIMINATE) score += 1;
    });

    // 2. Strategic Moat (Q2 Focus)
    const q2Tasks = tasks.filter(t => t.currentQuadrant === Quadrant.SCHEDULE);
    const q2Ratio = Math.round((q2Tasks.length / total) * 100);

    // 3. Action Velocity (7-day window)
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const recentAdded = tasks.filter(t => t.createdAt >= sevenDaysAgo).length;
    const recentCompleted = completedTasks.filter(t => (t.completedAt || 0) >= sevenDaysAgo).length;
    const velocity = recentAdded > 0 ? Number(((recentCompleted / recentAdded) * 100).toFixed(0)) : 100;

    // 4. Momentum Consistency
    const activeDays = new Set(completedTasks
      .filter(t => (t.completedAt || 0) >= sevenDaysAgo)
      .map(t => new Date(t.completedAt!).toLocaleDateString())
    ).size;
    const momentumConsistency = Math.round((activeDays / 7) * 100);

    // --- PERSONAL EFFECTIVENESS METRICS ---
    
    // Time to Completion (Average hours)
    const completedWithTimestamps = completedTasks.filter(t => t.completedAt);
    const timeToCompletion = completedWithTimestamps.length > 0 
      ? Number((completedWithTimestamps.reduce((acc, t) => acc + (t.completedAt! - t.createdAt), 0) / completedWithTimestamps.length / (1000 * 60 * 60)).toFixed(1))
      : 0;

    // Planning Accuracy (Completed vs Overdue/Unfinished within window)
    const tasksWithDueDates = tasks.filter(t => t.dueDate);
    const completedOnTime = tasksWithDueDates.filter(t => t.isCompleted && t.completedAt && t.completedAt <= t.dueDate!).length;
    const planningAccuracy = tasksWithDueDates.length > 0
      ? Math.round((completedOnTime / tasksWithDueDates.length) * 100)
      : 85; // Baseline default if no due dates

    // Focus Leverage (Weight of Q2 completions vs Q3/Q4 distractions)
    const q2Completions = completedTasks.filter(t => t.currentQuadrant === Quadrant.SCHEDULE).length;
    const q34Completions = completedTasks.filter(t => t.currentQuadrant === Quadrant.DELEGATE || t.currentQuadrant === Quadrant.ELIMINATE).length;
    const focusLeverage = q34Completions > 0 
      ? Number((q2Completions / q34Completions).toFixed(1))
      : q2Completions > 0 ? q2Completions : 1.0;

    // --- DECISION ENGINE INTELLIGENCE LAYER ---
    const totalUsersCount = users.length || 1;
    const premiumUsers = users.filter(u => u.tier === UserTier.PREMIUM);
    const premiumCount = premiumUsers.length;

    const mrr = premiumCount * 10;
    const arr = mrr * 12;

    const now = Date.now();
    const day = 24 * 60 * 60 * 1000;
    const dau = users.filter(u => (now - u.lastLogin) < day).length;
    const mau = users.filter(u => (now - u.lastLogin) < (30 * day)).length;
    const stickiness = mau > 0 ? Number(((dau / mau) * 100).toFixed(1)) : 0;

    const conversionRate = Number(((premiumCount / totalUsersCount) * 100).toFixed(1));
    const usersWithTasks = new Set(tasks.map(t => t.userId));
    const leaks = users.filter(u => !usersWithTasks.has(u.id)).length;

    const inactiveUsers = users.filter(u => (now - u.lastLogin) > (14 * day)).length;
    const newUsers = users.filter(u => (now - u.createdAt) < (7 * day)).length;
    let burnChurnSignal: 'STABLE' | 'WARNING' | 'CRITICAL' = 'STABLE';
    if (inactiveUsers > newUsers * 1.5) burnChurnSignal = 'CRITICAL';
    else if (inactiveUsers > newUsers) burnChurnSignal = 'WARNING';

    const usersWithSubtasks = new Set(tasks.filter(t => t.hasSubtasks).map(t => t.userId));
    const usersWithPartners = new Set(tasks.filter(t => (t.partners || []).length > 0).map(t => t.userId));
    const featurePenetration = {
      'AI Decomposition': Number(((usersWithSubtasks.size / totalUsersCount) * 100).toFixed(0)),
      'Accountability Relay': Number(((usersWithPartners.size / totalUsersCount) * 100).toFixed(0)),
      'Admin Decision Hub': 5 
    };

    const growthRate = velocity * 0.5;
    const profitMargin = mrr > 0 ? ((mrr - 200) / mrr) * 100 : 0; 
    const ruleOf40 = Number((growthRate + profitMargin).toFixed(1));

    return {
      prioriScore: score,
      deepWorkRatio: q2Ratio,
      questVelocity: velocity,
      momentumConsistency,
      timeToCompletion,
      planningAccuracy,
      focusLeverage,
      activeUsers: totalUsersCount,
      churnRate: 3.2,
      ltv: 450,
      cac: 120,
      mrr,
      arr,
      dau,
      mau,
      stickiness,
      conversionRate,
      leaks,
      burnChurnSignal,
      featurePenetration,
      burnRate: 200, 
      ltvCacRatio: 3.75,
      systemHealth: 99.9,
      latency: 18,
      burnMultiplier: 1.2,
      paybackMonths: 8,
      ruleOf40,
      errorRate: 0.04,
      growthVelocity: 14.5
    };
  },

  getDistributionByQuadrant(tasks: Task[]) {
    const dist = { Q1: 0, Q2: 0, Q3: 0, Q4: 0 };
    tasks.forEach(t => {
      dist[t.currentQuadrant]++;
    });
    return dist;
  },

  getDistributionByCategory(tasks: Task[], categories: any[]) {
    const dist: Record<string, number> = {};
    tasks.forEach(t => {
      dist[t.category] = (dist[t.category] || 0) + 1;
    });
    return Object.entries(dist).map(([id, count]) => {
      const cat = categories.find(c => c.id === id);
      return { name: cat?.name || 'Uncategorized', count, color: cat?.color || '#334155' };
    });
  },

  computeHistoricalSnapshots(tasks: Task[], metricKey: MetricKey, days: number = 30): DailySnapshot[] {
    const snapshots: DailySnapshot[] = [];
    const now = new Date();
    const dayMs = 24 * 60 * 60 * 1000;

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now.getTime() - i * dayMs);
      date.setHours(23, 59, 59, 999);
      const endOfDay = date.getTime();
      const dateStr = date.toISOString().split('T')[0];

      let value = 0;

      if (metricKey === 'prioriScore') {
        // Cumulative weighted completion points up to this day
        const completedByDay = tasks.filter(t =>
          (t.isCompleted || t.status === 'COMPLETED') && t.completedAt && t.completedAt <= endOfDay
        );
        completedByDay.forEach(t => {
          if (t.currentQuadrant === Quadrant.DO) value += 25;
          else if (t.currentQuadrant === Quadrant.SCHEDULE) value += 15;
          else if (t.currentQuadrant === Quadrant.DELEGATE) value += 5;
          else if (t.currentQuadrant === Quadrant.ELIMINATE) value += 1;
        });
      } else if (metricKey === 'questVelocity') {
        // Rolling 7-day completion ratio ending this day
        const windowStart = endOfDay - 7 * dayMs;
        const createdInWindow = tasks.filter(t => t.createdAt >= windowStart && t.createdAt <= endOfDay).length;
        const completedInWindow = tasks.filter(t =>
          (t.isCompleted || t.status === 'COMPLETED') && t.completedAt &&
          t.completedAt >= windowStart && t.completedAt <= endOfDay
        ).length;
        value = createdInWindow > 0 ? Math.round((completedInWindow / createdInWindow) * 100) : 100;
      } else if (metricKey === 'deepWorkRatio') {
        // Q2 percentage of all non-archived tasks existing by this day
        const existingByDay = tasks.filter(t => t.createdAt <= endOfDay && t.status !== 'ARCHIVED');
        const q2Count = existingByDay.filter(t => t.currentQuadrant === Quadrant.SCHEDULE).length;
        const total = existingByDay.length || 1;
        value = Math.round((q2Count / total) * 100);
      }

      snapshots.push({ date: dateStr, timestamp: endOfDay, value });
    }

    return snapshots;
  }
};
